/**
 * Pure trade inbox helpers — keep UI in sync with backend mutual-completion.
 *
 * Real flow (server):
 *   pending → (receiver accept) accepted → each party PUT /complete
 *   → only when BOTH senderCompleted & receiverCompleted → status=completed
 *
 * Design "SNAP / Trade Complete" must fire ONLY on full completion, never on
 * the first one-sided confirm.
 */

import { normalizeCondition } from "../utils/cardPricing";

export const idOf = (v) => String(v?._id || v || "");

export function bucketTrades(trades, myId) {
  const toRespond = [];
  const inProgress = [];
  const sent = [];
  const history = [];
  for (const t of trades || []) {
    const iAmSender = idOf(t.senderId) === String(myId);
    if (t.status === "pending" && !iAmSender) toRespond.push(t);
    else if (t.status === "pending" && iAmSender) sent.push(t);
    else if (t.status === "accepted") inProgress.push(t);
    else history.push(t);
  }
  return { toRespond, inProgress, sent, history };
}

/**
 * @returns {'confirm' | 'waiting' | 'done' | null}
 *   confirm — accepted & I have not confirmed yet
 *   waiting — I confirmed, other has not (still accepted)
 *   done    — status completed
 *   null    — not in completion phase
 */
export function completionPhase(trade, myId) {
  if (!trade) return null;
  if (trade.status === "completed") return "done";
  if (trade.status !== "accepted") return null;
  const iAmSender = idOf(trade.senderId) === String(myId);
  const myConfirmed = iAmSender ? !!trade.senderCompleted : !!trade.receiverCompleted;
  return myConfirmed ? "waiting" : "confirm";
}

/** Actions the current user may take on this trade card. */
export function availableActions(trade, myId) {
  if (!trade) return [];
  const iAmSender = idOf(trade.senderId) === String(myId);
  if (trade.status === "pending" && !iAmSender) return ["decline", "accept"];
  if (trade.status === "pending" && iAmSender) return ["cancel"];
  if (trade.status === "accepted") {
    // Either participant can raise a problem, whether or not they have already
    // confirmed their own side. A meet-up that falls through used to leave this
    // row as "In progress" forever with no way out.
    //
    // Walking away ("abandon") closes once the OTHER side has confirmed the
    // swap: owner decision Q-24, which the server enforces with a 409. The
    // same rule as tradeJourneyLogic's `canMarkFellThrough`, written out here
    // because that module imports this one.
    const phase = completionPhase(trade, myId);
    const theyConfirmed = iAmSender ? !!trade.receiverCompleted : !!trade.senderCompleted;
    const exits = theyConfirmed ? ["dispute"] : ["abandon", "dispute"];
    if (phase === "confirm") return ["complete", ...exits];
    return exits;
  }
  return [];
}

/**
 * After a /complete response, decide snack + whether to show celebration.
 * @param {object} before - trade before the call
 * @param {object} after - trade returned by API (or merged)
 */
export function interpretCompleteResult(before, after, myId) {
  // A completion whose card transfer came up short is moved to `disputed` by
  // the server (launch invariant, P0 area A) — never celebrate it.
  if (after?.status === "disputed") {
    return {
      snack: "We couldn't confirm the card handover — this trade is under review.",
      showCelebration: false,
      phase: "review",
    };
  }
  const phaseAfter = completionPhase(after || before, myId);
  if (phaseAfter === "done" || after?.status === "completed") {
    return {
      snack: "Trade complete — nice swap.",
      showCelebration: true,
      phase: "done",
    };
  }
  return {
    snack: "You confirmed — waiting for the other trader.",
    showCelebration: false,
    phase: "waiting",
  };
}

/**
 * Count of trades that need THIS user to act right now — the definition the
 * nav badge uses (see navConfig.js: a badge means action needed, never
 * inventory). Two cases:
 *   - pending incoming requests (toRespond)
 *   - accepted trades where the other side already confirmed completion and
 *     I have not — NOT every accepted trade in "confirm" phase, since most of
 *     those are just an in-flight meet-up nobody is waiting on yet.
 */
export function pendingActionCount(trades, myId) {
  return pendingActionTradeIds(trades, myId).length;
}

/** The trades `pendingActionCount` counts, by id. */
/**
 * An open meetup suggestion that only I can answer (T-71, tablet QA 2026-10-23):
 * accepted, a proposal on the table, and not proposed by me. The trade page
 * already says "They've suggested a meetup — That works", and a
 * `trade_meetup_proposed` notification goes out, but the badge and the "Needs
 * you" tab did not count it. Reads the summary's `meetupProposedBy` or a full
 * trade's `meetup.proposal.proposedBy`, whichever the caller holds.
 */
export function awaitsMyMeetupAnswer(t, myId) {
  if (!t || t.status !== "accepted") return false;
  const by = t.meetupProposedBy != null ? String(t.meetupProposedBy) : idOf(t.meetup?.proposal?.proposedBy);
  return !!by && by !== String(myId);
}

export function pendingActionTradeIds(trades, myId) {
  const { toRespond, inProgress } = bucketTrades(trades, myId);
  const waitingOnMe = inProgress.filter((t) => {
    if (completionPhase(t, myId) !== "confirm") return false;
    const iAmSender = idOf(t.senderId) === String(myId);
    const otherCompleted = iAmSender ? !!t.receiverCompleted : !!t.senderCompleted;
    return otherCompleted;
  });
  const meetupForMe = inProgress.filter((t) => awaitsMyMeetupAnswer(t, myId) && !waitingOnMe.includes(t));
  return [...toRespond, ...waitingOnMe, ...meetupForMe].map((t) => String(t._id));
}

/**
 * The Trades tab badge (P0 area C): how many DISTINCT trades either need you
 * right now or have an update you have not opened yet.
 *
 * `pendingActionCount` alone left most trade events invisible in the app — an
 * accepted offer, a suggested meetup, a decline, a cancellation, a trade put
 * under review — so a collector who did not happen to open the right trade
 * never learned of them. An unread `trade_*` notification now badges its
 * trade until the trade is opened (TradeJourneyPage marks it read). Counted
 * by trade, so a trade that is both waiting on you and has an unread update
 * counts once.
 *
 * The server's `unreadTradeIds` covers every page; acting on a trade (accept,
 * decline, confirm, propose) marks it seen server-side, so an answered trade
 * does not keep badging.
 */
export function tradesBadgeCount(actionTradeIds, notifications, unreadTradeIds = null) {
  const ids = new Set((actionTradeIds || []).map(String));
  for (const id of tradesWithUnreadUpdate(notifications, unreadTradeIds)) ids.add(id);
  return ids.size;
}

/**
 * Trade ids with an unread update. The server's `unreadTradeIds` covers every
 * unread row; the loaded page is only the fallback for an older server — it
 * undercounts once chat pushes a trade update past the first page (skeptic,
 * launch sprint).
 */
export function tradesWithUnreadUpdate(notifications, unreadTradeIds = null) {
  if (Array.isArray(unreadTradeIds)) return new Set(unreadTradeIds.map(String));
  const ids = new Set();
  for (const n of notifications || []) {
    if (!n || n.read) continue;
    if (n.relatedModel !== "TradeRequest" || !n.relatedId) continue;
    if (typeof n.type !== "string" || !n.type.startsWith("trade_")) continue;
    ids.add(String(n.relatedId));
  }
  return ids;
}

/* ─────────────────────────── trade eligibility (#147) ─────────────────────── */

/**
 * Cards the server will actually accept as an offer: Trade Binder ("trade") and
 * allowed sale listings only. Vault ("glory") cards are rejected server-side, so
 * the offer picker must never surface them — otherwise a user builds a whole
 * offer around a card that can only fail on submit.
 *
 * Pure and defensive: takes the shape getUserCollection returns
 * ({ glory, trade, sale }) and returns the eligible-to-offer cards.
 */
export function eligibleOfferCards(collection) {
  if (!collection) return [];
  return [...(collection.trade || []), ...(collection.sale || [])];
}

/**
 * The grade an offered card is being offered AT.
 *
 * ONE resolver for the whole builder, because the slot chip, the fairness
 * total and the persisted offer snapshot are three readings of the same claim
 * — when they each wrote their own fallback they stopped agreeing, and the
 * receiver was shown a condition the sender never stated.
 *
 * Precedence: the sender's live choice (they cycled the chip) over the grade
 * the owner already recorded on the card. Normalized on the way out so a
 * document carrying a legacy value (`good`, `excellent`) is offered as its
 * canonical equivalent and never re-persists a deprecated grade;
 * `normalizeCondition` supplies DEFAULT_CONDITION when a card has no grade at
 * all.
 *
 * @param {object} item — UserCard / collection entry being offered
 * @param {object} [cardConditions] — builder state, keyed by UserCard id
 */
export function offeredCondition(item, cardConditions = {}) {
  return cardConditions?.[item?._id] || normalizeCondition(item?.condition);
}

/** Reason codes mirrored from server/trades/tradeValidationService.js (#147). */
export const TRADE_ERROR_CODES = {
  CANNOT_TRADE_SELF: "CANNOT_TRADE_SELF",
  RECEIVER_INVALID: "RECEIVER_INVALID",
  NO_OFFERED_CARDS: "NO_OFFERED_CARDS",
  CARD_ID_INVALID: "CARD_ID_INVALID",
  BLOCKED: "BLOCKED",
  DUPLICATE_OFFERED_CARD: "DUPLICATE_OFFERED_CARD",
  DUPLICATE_REQUESTED_CARD: "DUPLICATE_REQUESTED_CARD",
  CARD_NOT_OWNED: "CARD_NOT_OWNED",
  CARD_NOT_TRADEABLE: "CARD_NOT_TRADEABLE",
  REQUESTED_CARD_UNAVAILABLE: "REQUESTED_CARD_UNAVAILABLE",
  CHALLENGE_REQUIRED: "CHALLENGE_REQUIRED",
  TRADE_EXPIRED: "TRADE_EXPIRED",
  DUPLICATE_PENDING_TRADE: "DUPLICATE_PENDING_TRADE",
};

/**
 * Turn ONE structured trade error into user-facing copy.
 *
 * Pure by construction: the caller passes `resolveCardName(cardId) → string|null`
 * so this function never touches the network and — critically — never has to
 * fall back to printing a raw id. When a name can't be resolved it says
 * "One of your/their cards", never the ObjectId. A photo challenge is mentioned
 * ONLY for CHALLENGE_REQUIRED, never guessed at.
 *
 * @param {{code:string, side?:string, cardId?:string}} error
 * @param {{resolveCardName?:(id:string)=>?string, traderName?:string}} ctx
 */
export function describeTradeError(error, { resolveCardName, traderName } = {}) {
  const code = error?.code;
  const who = traderName || "this collector";
  const named = error?.cardId ? (resolveCardName?.(error.cardId) || null) : null;
  const yours = named || "One of your cards";
  const theirs = named || "One of their cards";

  switch (code) {
    case TRADE_ERROR_CODES.CARD_NOT_TRADEABLE:
      return `${yours} can't be offered yet — it's in your Vault. Move it to your Trade Binder first.`;
    case TRADE_ERROR_CODES.CARD_NOT_OWNED:
      return `${yours} is no longer in your collection.`;
    case TRADE_ERROR_CODES.REQUESTED_CARD_UNAVAILABLE:
      return `${theirs} is no longer available from ${who}. They may have removed it from their Trade Binder.`;
    case TRADE_ERROR_CODES.CHALLENGE_REQUIRED:
      return `${yours} needs a photo challenge before it can be traded.`;
    case TRADE_ERROR_CODES.CARD_ID_INVALID:
      return `${error?.side === "requested" ? theirs : yours} couldn't be read. Please remove and re-add it.`;
    case TRADE_ERROR_CODES.DUPLICATE_OFFERED_CARD:
    case TRADE_ERROR_CODES.DUPLICATE_REQUESTED_CARD:
      return "A card is listed twice. Please remove the duplicate.";
    case TRADE_ERROR_CODES.NO_OFFERED_CARDS:
      return "Add at least one card to your offer.";
    case TRADE_ERROR_CODES.CANNOT_TRADE_SELF:
      return "You can't trade with yourself.";
    case TRADE_ERROR_CODES.BLOCKED:
      return `You can't send a trade to ${who}.`;
    case TRADE_ERROR_CODES.RECEIVER_INVALID:
      return `${who} can't receive trades right now.`;
    case TRADE_ERROR_CODES.TRADE_EXPIRED:
      return "This trade has expired. Please start a new one.";
    case TRADE_ERROR_CODES.DUPLICATE_PENDING_TRADE:
      return `You already have a pending trade with ${who}. Wait for a response before sending another.`;
    default:
      return "This trade can't be sent as-is. Please review your cards and try again.";
  }
}

/**
 * Map a whole structured `tradeErrors` array to de-duplicated user copy.
 * Returns [] when there is nothing structured to show (caller falls back to the
 * plain message).
 */
export function describeTradeErrors(tradeErrors, ctx) {
  const list = Array.isArray(tradeErrors) ? tradeErrors : [];
  const lines = list.map((e) => describeTradeError(e, ctx));
  return [...new Set(lines)];
}

/** Card image URLs for celebration / thumbs (offered + requested). */
/**
 * Card names for each side, in the same order as tradeCardImages, so a row can
 * say what is on the table in words and its thumbnails can carry alt text.
 * Two trades with the same collector otherwise look identical. (E2E E-13)
 */
export function tradeCardNames(trade, myId) {
  if (!trade) return { mine: [], theirs: [] };
  const iAmSender = idOf(trade.senderId) === String(myId);
  const side = (list) =>
    (list || []).filter((it) => it.cardId?.images?.small || it.cardId?.images?.large).map((it) => it.cardId?.name || "");
  const offered = side(trade.offeredCards);
  const requested = side(trade.requestedCards);
  return iAmSender ? { mine: offered, theirs: requested } : { mine: requested, theirs: offered };
}

/**
 * The photos to show for one trade entry (E2E audit 4·4, 3·10): the snapshot
 * the sender attached, else the listing photos of the copy the entry names,
 * which the server sends as `listingPhotos` while the trade is open and the
 * copy is still listed. A requested card has no snapshot, so it used to show
 * "No verification photos provided" beside a listing that has photos.
 */
export function entryPhotos(entry) {
  if (Array.isArray(entry?.photos) && entry.photos.length > 0) return entry.photos;
  return Array.isArray(entry?.listingPhotos) ? entry.listingPhotos : [];
}

export function tradeCardImages(trade, myId) {
  if (!trade) return { mine: [], theirs: [] };
  const iAmSender = idOf(trade.senderId) === String(myId);
  const offered = (trade.offeredCards || []).map((it) => it.cardId?.images?.small || it.cardId?.images?.large).filter(Boolean);
  const requested = (trade.requestedCards || []).map((it) => it.cardId?.images?.small || it.cardId?.images?.large).filter(Boolean);
  return iAmSender
    ? { mine: offered, theirs: requested }
    : { mine: requested, theirs: offered };
}
